import { ampli } from '@/ampli';
import Image, { ImageProps } from 'next/image';
import { useMemo } from 'react';
import { useMedia } from 'react-use';

import { TokenContentLinkType } from '@/lib/api/tokens/types';

import { CHAIN_EXPLORER_NAMES } from '@/constants/chain';

import { ZoraToken } from '@/hooks/useZora';

import { buttons } from '@/styles/buttons';

import { BinanceIcon } from '@/ui/Icons/BinanceIcon';
import { ChainExplorerIcons } from '@/ui/Icons/ChainExplorerIcon';
import { CoinbaseIcon } from '@/ui/Icons/CoinbaseIcon';
import { CoinGeckoIcon } from '@/ui/Icons/CoinGeckoIcon';
import { CoinMarketCapIcon } from '@/ui/Icons/CoinMarketCapIcon';
import { DiscordIcon } from '@/ui/Icons/DiscordIcon';
import { ExplorerIcon } from '@/ui/Icons/ExplorerIcon';
import { GlobeIcon } from '@/ui/Icons/GlobeIcon';
import { RWAIcon } from '@/ui/Icons/RWAIcon';
import { TelegramIcon } from '@/ui/Icons/TelegramIcon';
import { TikTokIcon } from '@/ui/Icons/TikTokIcon';
import { TwitterIcon } from '@/ui/Icons/TwitterIcon';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { getBlockExplorerTokenURL } from '@/utils/blockExplorers';
import type { Links, Ticker } from '@/utils/coingecko/types';
import { ChainAddress } from '@/utils/models';
import { isZoraCreatorCoin, isZoraToken } from '@/utils/zora';

import { fontStyles } from '../../../styles/typography';
import { tokenProfileHeaderBreakpoints } from '../TokenProfileHeader/responsive';
import { TokenProfileLinksSkeleton } from '../TokenProfileHeader/TokenProfileHeaderLoader';
import { SecurityAuditPreview } from '../TokenProfileSecurityAudit/SecurityAuditPreview';
import { LiquidityScore } from './LiquidityScore';

interface TokenProfileLinksProps {
  /** Active token data. */
  activeToken: TokenResult;
  /** CoinGecko Token ID. */
  coingeckoId?: string;
  /** Token contract address */
  address: ChainAddress;
  /** Token's network ID */
  chainId: number;
  /** Whether the component is fetching data. */
  isLoading: boolean;
  /** A CoinGecko `Links` object. */
  coingeckoLinks: Links | undefined;
  /** A CoinGecko `Tickers` object. */
  coingeckoTickers: Ticker[] | undefined;
  /** Token Content API `Links` array. */
  tokenContentLinks?: TokenContentLinkType[] | undefined;
  /** Zora Token info */
  zora?: ZoraToken;
  /** Whether the token has been tokenized by Superstate */
  isSuperstate: boolean;
}

const ZoraIcon = (props: Omit<ImageProps, 'src' | 'alt'>) => (
  <Image src="/images/tokens/zora.webp" alt="Zora" width={16} height={16} {...props} />
);

const TokenProfileLinks: React.FC<TokenProfileLinksProps> = ({
  activeToken,
  coingeckoLinks,
  tokenContentLinks,
  coingeckoTickers,
  isLoading,
  coingeckoId,
  chainId,
  address,
  zora,
  isSuperstate,
}) => {
  const isMobileButtonView = useMedia(tokenProfileHeaderBreakpoints.mobileLinks, false);
  const isZora = isZoraToken(activeToken);
  const isZoraCreator = isZoraCreatorCoin(activeToken);
  const zoraType = isZora ? (isZoraCreator ? 'creator' : 'post') : undefined;
  const linkMap = useMemo(() => {
    const linksMap: Record<string, string | undefined> = {};
    const addLink = (type: string, value: string | undefined) => {
      if (!linksMap[type]) {
        linksMap[type] = value;
      }
    };
    if (tokenContentLinks && tokenContentLinks.length > 0) {
      tokenContentLinks.forEach((link) => {
        if (link.linkType && link.src) {
          linksMap[link.linkType] = link.src;
        }
      });
    }
    addLink('explorer', getBlockExplorerTokenURL(chainId, address));
    if (coingeckoLinks) {
      addLink('website', coingeckoLinks.homepage?.[0]);
      addLink('forum', coingeckoLinks.official_forum_url?.[0]);
      addLink('chat', coingeckoLinks.chat_url?.[0]);
      addLink('announcement', coingeckoLinks.announcement_url?.[0]);
      addLink(
        'twitter',
        coingeckoLinks.twitter_screen_name
          ? `https://x.com/${coingeckoLinks.twitter_screen_name}`
          : undefined,
      );
      addLink(
        'facebook',
        coingeckoLinks.facebook_username
          ? `https://www.facebook.com/${coingeckoLinks.facebook_username}`
          : undefined,
      );
      addLink(
        'telegram',
        coingeckoLinks.telegram_channel_identifier
          ? `https://t.me/${coingeckoLinks.telegram_channel_identifier}`
          : undefined,
      );
      addLink(
        'reddit',
        // only render reddit links if there is a subreddit
        coingeckoLinks.subreddit_url?.endsWith('reddit.com')
          ? undefined
          : coingeckoLinks.subreddit_url,
      );
      addLink('github', coingeckoLinks.repos_url?.github?.[0]);
    }
    if (coingeckoId) {
      linksMap['coingecko'] = `https://www.coingecko.com/en/coins/${coingeckoId}`;
    }
    if (coingeckoTickers) {
      addLink(
        'coinbase',
        coingeckoTickers?.filter((ticker) => ticker.market?.name === 'Coinbase Exchange')[0]
          ?.trade_url,
      );
      addLink(
        'binance',
        coingeckoTickers?.filter((ticker) => ticker.market?.name === 'Binance')[0]?.trade_url,
      );
    }
    // Add Zora link
    if (isZora && zora) {
      if (isZoraCreator && zora.profile?.handle) {
        addLink('zora', `https://zora.co/@${zora.profile.handle}`);
      } else if (zora.coin?.address) {
        addLink('zora', `https://zora.co/coin/base:${zora.coin.address}`);
      }

      // Add creator social links for Zora tokens
      if (zora.profile?.socialAccounts) {
        if (zora.profile.socialAccounts.twitter?.displayName) {
          addLink('twitter', `https://x.com/${zora.profile.socialAccounts.twitter.displayName}`);
        }
        if (zora.profile.socialAccounts.tiktok?.displayName) {
          addLink(
            'tiktok',
            `https://www.tiktok.com/@${zora.profile.socialAccounts.tiktok.displayName}`,
          );
        }
      }
    }

    if (isSuperstate) {
      addLink('rwa', `https://app.rwa.xyz/assets/${activeToken.symbol}`);
    }

    return linksMap;
  }, [
    tokenContentLinks,
    activeToken.symbol,
    chainId,
    address,
    coingeckoLinks,
    coingeckoId,
    coingeckoTickers,
    isZora,
    isZoraCreator,
    isSuperstate,
    zora,
  ]);

  if (isLoading) {
    return <TokenProfileLinksSkeleton />;
  }

  const renderLink = (
    type: string,
    Icon: React.ComponentType<{ className?: string }>,
    label: string,
  ) => {
    const href = linkMap[type];
    const name =
      label === 'Explorer'
        ? // Except POL and MNT, all native tokens should have undefined href
          href
          ? CHAIN_EXPLORER_NAMES[chainId]
          : "Native tokens don't have contract addresses."
        : label;
    const onClick = () => {
      ampli.tokenOfficialLinkClicked({
        link_type: type,
        token_address: activeToken.address,
        token_chain_id: activeToken.chainId,
        token_chain_name: activeToken.chainName,
      });
    };

    if (href) {
      return (
        <MatchaTooltip>
          <MatchaTooltipTrigger>
            <a
              className={buttons({
                color: 'gray',
                shape: 'pill',
                class:
                  'bg-token-gray-300 w-8 h-8 p-2 mb-1 min-tablet:max-tablet-desktop-links:mt-4 max-mobile-scrollable-links:first:ml-4 max-mobile-scrollable-links:last:mr-4 shrink-0 mx-1',
              })}
              href={href}
              target="_blank"
              rel="noreferrer"
              onClick={onClick}
            >
              <Icon />
            </a>
          </MatchaTooltipTrigger>
          <MatchaTooltipContent>{name}</MatchaTooltipContent>
          <MatchaTooltipPopover sideOffset={8} className="max-w-[168px] flex tablet:text-center" />
        </MatchaTooltip>
      );
    }
    return (
      <MatchaTooltip>
        <MatchaTooltipTrigger>
          <button
            className={buttons({
              color: 'transparent',
              shape: 'pill',
              class:
                'w-8 h-8 p-2 mb-1 bg-token-gray-400 opacity-30 min-tablet:max-tablet-desktop-links:mt-4 max-mobile-scrollable-links:first:ml-4 max-mobile-scrollable-links:last:mr-4 shrink-0 mx-1',
            })}
            disabled={true}
          >
            <Icon />
          </button>
        </MatchaTooltipTrigger>
        <MatchaTooltipContent>{name}</MatchaTooltipContent>
        <MatchaTooltipPopover sideOffset={8} className="max-w-[168px] flex md:text-center" />
      </MatchaTooltip>
    );
  };

  const dividerClassName =
    'w-px h-[51px] mx-3 bg-gradient-to-b from-token-gray-300/30 via-token-gray-300/70 to-transparent';

  return (
    <>
      <div className="flex flex-row tablet:pl-0 justify-between border-token-gray-200 border-solid overflow-x-auto overflow-y-hidden relative scrollbar-none border-b last:border-b-0 -mx-4 px-4 tablet:mask-none tablet:mx-0 mt-3 tablet:justify-start [mask-image:linear-gradient(to_right,rgba(0,0,0,0)_0%,rgba(0,0,0,1)_10%,rgba(0,0,0,1)_90%,rgba(0,0,0,0)_100%)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {!isMobileButtonView ? (
          <>
            <SecurityAuditPreview activeToken={activeToken} isLoading={isLoading} />
            <div className={dividerClassName}></div>
            <LiquidityScore
              activeToken={{
                address: activeToken.address,
                chainId: activeToken.chainId,
                chainName: activeToken.chainName,
                numBlockedTokenLists: activeToken.tokenListsBlock?.length ?? 0,
                zoraType,
                symbol: activeToken.symbol,
              }}
            />
            <div className={dividerClassName}></div>
            <div>
              <div
                className={fontStyles({
                  variant: 'LABEL_XS',
                  class: [
                    'text-token-gray-800',
                    'flex',
                    'min-w-fit',
                    'font-normal',
                    'ml-1',
                    'items-center',
                    'mb-[7px]',
                  ],
                })}
              >
                Official links
              </div>
              {renderLink('explorer', ChainExplorerIcons[chainId] || ExplorerIcon, 'Explorer')}
              {isZora && renderLink('zora', ZoraIcon, 'Zora')}
              {!isZora && (
                <>
                  {renderLink('website', GlobeIcon, 'Website')}
                  {renderLink('twitter', TwitterIcon, 'X.com')}
                  {!isSuperstate && renderLink('telegram', TelegramIcon, 'Telegram')}
                  {!isSuperstate && renderLink('discord', DiscordIcon, 'Discord')}
                </>
              )}
            </div>
            {isZora &&
              (zora?.profile?.socialAccounts?.twitter?.displayName ||
                zora?.profile?.socialAccounts?.tiktok?.displayName) && (
                <>
                  <div className={dividerClassName}></div>
                  <div>
                    <div
                      className={fontStyles({
                        variant: 'LABEL_XS',
                        class: [
                          'text-token-gray-800',
                          'flex',
                          'min-w-fit',
                          'font-normal',
                          'ml-1',
                          'items-center',
                          'mb-[7px]',
                        ],
                      })}
                    >
                      Creator profiles
                    </div>
                    {zora?.profile?.socialAccounts?.twitter?.displayName &&
                      renderLink('twitter', TwitterIcon, 'X.com')}
                    {zora?.profile?.socialAccounts?.tiktok?.displayName &&
                      renderLink('tiktok', TikTokIcon, 'TikTok')}
                  </div>
                </>
              )}
            {!isZora && (
              <>
                <div className={dividerClassName}></div>
                <div>
                  <div
                    className={fontStyles({
                      variant: 'LABEL_XS',
                      class: [
                        'text-token-gray-800',
                        'flex',
                        'min-w-fit',
                        'font-normal',
                        'ml-1',
                        'items-center',
                        'mb-[7px]',
                      ],
                    })}
                  >
                    Trackers
                  </div>
                  {renderLink('coingecko', CoinGeckoIcon, 'CoinGecko')}
                  {renderLink('coinmarketcap', CoinMarketCapIcon, 'CoinMarketCap')}
                  {isSuperstate && renderLink('rwa', RWAIcon, 'RWA.xyz')}
                </div>
                <div className={dividerClassName}></div>
                <div>
                  <div
                    className={fontStyles({
                      variant: 'LABEL_XS',
                      class: [
                        'text-token-gray-800',
                        'flex',
                        'min-w-fit',
                        'font-normal',
                        'ml-1',
                        'items-center',
                        'mb-[7px]',
                      ],
                    })}
                  >
                    Exchange
                  </div>
                  {renderLink('coinbase', CoinbaseIcon, 'Coinbase')}
                  {renderLink('binance', BinanceIcon, 'Binance')}
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <SecurityAuditPreview activeToken={activeToken} isLoading={isLoading} />
            <LiquidityScore
              activeToken={{
                address: activeToken.address,
                chainId: activeToken.chainId,
                chainName: activeToken.chainName,
                numBlockedTokenLists: activeToken.tokenListsBlock?.length ?? 0,
                zoraType,
                symbol: activeToken.symbol,
              }}
            />
            {renderLink('explorer', ChainExplorerIcons[chainId] || ExplorerIcon, 'Explorer')}
            {isZora && renderLink('zora', ZoraIcon, 'Zora')}
            {isZora ? (
              <>
                {zora?.profile?.socialAccounts?.twitter?.displayName &&
                  renderLink('twitter', TwitterIcon, 'X.com')}
                {zora?.profile?.socialAccounts?.tiktok?.displayName &&
                  renderLink('tiktok', TikTokIcon, 'TikTok')}
              </>
            ) : (
              <>
                {renderLink('website', GlobeIcon, 'Website')}
                {renderLink('twitter', TwitterIcon, 'X.com')}
                {!isSuperstate && renderLink('telegram', TelegramIcon, 'Telegram')}
                {!isSuperstate && renderLink('discord', DiscordIcon, 'Discord')}
                {renderLink('coinbase', CoinbaseIcon, 'Coinbase')}
                {renderLink('binance', BinanceIcon, 'Binance')}
                {renderLink('coingecko', CoinGeckoIcon, 'CoinGecko')}
                {renderLink('coinmarketcap', CoinMarketCapIcon, 'CoinMarketCap')}
                {isSuperstate && renderLink('rwa', RWAIcon, 'RWA.xyz')}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default TokenProfileLinks;
